import { useState } from 'react'
import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectValue,
  SelectTrigger,
  Slider,
  Radio,
} from '@you/ui'
import { Textarea } from '@/components/ui/textarea'
import { Icon } from '@/components/Icon'
import Avatar from '@/components/common/Avatar'
import NumberInput from '@/components/Agents/NumberInput'
import KnowledgeBaseDialog from '@/components/Agents/KnowledgeBaseDialog'
import { KnowledgeDocType, KnowledgeBase } from '@/api/types/knowledge.types'
import type { BaseAgentRequest } from '@/api/types/agent.type'
import { AgentType } from '@/api/types/agent.type'
import { AGENT_CATEGORY_OPTIONS } from '@/constants/agent'
import { useEffect } from 'react'
import { knowledgeService } from '@/api/services/knowledge.service'

// 表单数据类型
interface AgentFormData {
  name: string
  category: number
  setting: string
  description: string
  greeting: string
  questionSuggestions: string[]
  model: number
  temperature: number
  roundsContext: number
  maxReplyTokens: number
  skills: number[]
  knowledgeBase: KnowledgeDocType
  icon: string
  knowledgeBaseIdList: string[] // 添加知识库ID列表
}

interface AgentFormProps {
  initialData?: Partial<AgentFormData>
  onSubmit: (data: BaseAgentRequest) => Promise<void>
  onCancel: () => void
  submitButtonText?: string
  cancelButtonText?: string
  workspaceId: string
  title?: string
  subtitle?: string
}

const AgentForm = ({
  initialData,
  onSubmit,
  onCancel,
  submitButtonText = '创建',
  cancelButtonText = '取消',
  workspaceId,
  title = '新建智能体',
  subtitle,
}: AgentFormProps) => {
  // 表单状态
  const [formData, setFormData] = useState<AgentFormData>({
    name: '',
    category: AgentType.WRITING,
    setting: '',
    description: '',
    greeting: '',
    questionSuggestions: [''],
    model: 0,
    temperature: 0.7,
    roundsContext: 5,
    maxReplyTokens: 2000,
    skills: [],
    knowledgeBase: KnowledgeDocType.TEXT,
    icon: '',
    knowledgeBaseIdList: [], // 初始化为空数组
    ...initialData,
  })
  const [knowledgeDialogOpen, setKnowledgeDialogOpen] = useState(false)
  const [selectedKnowledgeBases, setSelectedKnowledgeBases] = useState<
    KnowledgeBase[]
  >([])
  const [agentIcon, setAgentIcon] = useState<string>('')

  // 根据knowledgeBaseIdList获取知识库详情并反显
  useEffect(() => {
    const fetchKnowledgeBases = async () => {
      if (
        formData.knowledgeBaseIdList &&
        formData.knowledgeBaseIdList.length > 0
      ) {
        try {
          // 获取当前workspace下的所有知识库
          const response = await knowledgeService.getKnowledgeList({
            workspaceId: workspaceId,
          })

          // 过滤出对应的知识库
          const knowledgeBases = response.knowledgeBases
            .filter((knowledge) =>
              formData.knowledgeBaseIdList.includes(knowledge.id)
            )
            .map(
              (knowledge) =>
                ({
                  id: knowledge.id,
                  name: knowledge.name,
                  docType: knowledge.docType,
                } as KnowledgeBase)
            )

          setSelectedKnowledgeBases(knowledgeBases)
        } catch (error) {
          console.error('获取知识库详情失败:', error)
        }
      }
    }

    fetchKnowledgeBases()
  }, [formData.knowledgeBaseIdList, workspaceId])

  // 智能体分类选项 - 使用常量
  const categoryOptions = AGENT_CATEGORY_OPTIONS

  // 大模型选项
  const modelOptions = [
    { value: 0, label: '通义千问' },
    { value: 1, label: 'deepseek' },
    { value: 2, label: 'gpt-5' },
  ]

  // 技能选项
  const skillOptions = [
    { id: 1, label: '文生图', name: 'image-generation' },
    { id: 2, label: '网页搜索', name: 'web-search' },
    { id: 3, label: '代码执行器', name: 'code-executor' },
  ]

  // 知识库选项
  const knowledgeBaseOptions = [
    { value: KnowledgeDocType.TEXT, label: '文本类', disabled: false },
    { value: KnowledgeDocType.TABLE, label: '表格类', disabled: true },
  ]

  // 处理输入变化
  const handleInputChange = (
    field: keyof AgentFormData,
    value: string | number | string[]
  ) => {
    setFormData((prev) => {
      const newFormData = { ...prev, [field]: value }

      // 如果切换了知识库类型，清空已选的知识库
      if (field === 'knowledgeBase' && prev.knowledgeBase !== value) {
        setSelectedKnowledgeBases([])
      }

      return newFormData
    })
  }

  // 处理技能选择
  const handleSkillToggle = (skillId: number) => {
    setFormData((prev) => ({
      ...prev,
      skills: prev.skills.includes(skillId)
        ? prev.skills.filter((id) => id !== skillId)
        : [...prev.skills, skillId],
    }))
  }

  // 处理提问灵感添加
  const handleAddQuestionSuggestion = () => {
    setFormData((prev) => ({
      ...prev,
      questionSuggestions: [...prev.questionSuggestions, ''],
    }))
  }

  // 处理提问灵感删除
  const handleRemoveQuestionSuggestion = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      questionSuggestions: prev.questionSuggestions.filter(
        (_, i) => i !== index
      ),
    }))
  }

  // 处理提问灵感输入变化
  const handleQuestionSuggestionChange = (index: number, value: string) => {
    setFormData((prev) => ({
      ...prev,
      questionSuggestions: prev.questionSuggestions.map((item, i) =>
        i === index ? value : item
      ),
    }))
  }

  // 处理知识库对话框打开
  const handleKnowledgeDialogOpen = () => {
    setKnowledgeDialogOpen(true)
  }

  // 处理知识库选择确认
  const handleKnowledgeConfirm = (knowledgeBases: KnowledgeBase[]) => {
    setSelectedKnowledgeBases(knowledgeBases)
  }

  // 处理头像变化
  const handleAvatarChange = (avatarUrl: string) => {
    setAgentIcon(avatarUrl)
  }

  // 处理提交
  const handleSubmit = async () => {
    try {
      // 构建BaseAgentRequest参数
      const agentRequest: BaseAgentRequest = {
        name: formData.name,
        description: formData.description,
        setting: formData.setting,
        greeting: formData.greeting,
        icon: agentIcon,
        questionSuggestions: formData.questionSuggestions,
        modelId: formData.model,
        modelSetting: {
          temperature: formData.temperature,
          roundsContext: formData.roundsContext,
          maxReplyTokens: formData.maxReplyTokens,
        },
        skillType: formData.skills,
        type: formData.category,
        knowledgeBaseIds: selectedKnowledgeBases.map((kb) => kb.id),
        workspaceId: workspaceId,
        // isPublish: 0, // 默认为草稿状态
      }

      console.log('提交智能体数据', agentRequest)

      // 调用父组件的提交方法
      await onSubmit(agentRequest)
    } catch (error) {
      console.error('提交智能体失败:', error)
      // TODO: 显示错误提示
    }
  }

  const handleRemoveKnowledge = (knowledgeId: string) => {
    setSelectedKnowledgeBases((prev) =>
      prev.filter((knowledge) => knowledge.id !== knowledgeId)
    )
  }

  return (
    <div className="min-h-screen">
      {/* 头部 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[24px] font-[500] text-[#000000]">{title}</h1>
          {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
        </div>
        <div className="flex items-center space-x-3">
          <Button
            variant="outline"
            onClick={onCancel}
            className="bg-transparent rounded-full"
          >
            {cancelButtonText}
          </Button>
          <Button
            onClick={handleSubmit}
            className="rounded-full gradient-primary"
          >
            {submitButtonText}
          </Button>
        </div>
      </div>

      {/* 内容区域 */}
      <div className="flex justify-center pb-20">
        <div className="space-y-6 w-[765px] mt-auto">
          {/* 头像选择 */}
          <Avatar
            initialAvatar={formData.icon}
            onAvatarChange={handleAvatarChange}
          />

          {/* 名称 */}
          <div>
            <label className="block text-m text-[#555555] mb-2">名称</label>
            <Input
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="给您的智能体设置一个名字~"
              className="w-full placeholder:text-[#C7C7C7]"
            />
          </div>

          {/* 智能体分类 */}
          <div>
            <label className="block text-m text-[#555555] mb-2">
              智能体分类
            </label>
            <Select
              value={formData.category.toString()}
              onValueChange={(value) => handleInputChange('category', value)}
            >
              <SelectTrigger className="w-full h-9 bg-white rounded-md border border-input px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring">
                <SelectValue placeholder="请选择智能体分类" />
              </SelectTrigger>
              <SelectContent>
                {categoryOptions.map((option) => (
                  <SelectItem
                    key={option.value}
                    value={option.value.toString()}
                  >
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* 设定 */}
          <div>
            <label className="block text-m text-[#555555] mb-2">设定</label>
            <Textarea
              value={formData.setting}
              onChange={(e) => handleInputChange('setting', e.target.value)}
              placeholder="请输入智能体设定"
              rows={4}
              className="w-full bg-white placeholder:text-[#C7C7C7]"
            />
          </div>

          {/* 智能体简介 */}
          <div>
            <label className="block text-m text-[#555555] mb-2">
              智能体简介
            </label>
            <Textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="请输入智能体简介（最多500字）"
              rows={4}
              maxLength={500}
              className="w-full bg-white placeholder:text-[#C7C7C7]"
            />
            <div className="text-xs text-gray-400 mt-1 text-right">
              {formData.description.length}/500
            </div>
          </div>

          {/* 开场白 */}
          <div>
            <label className="block text-m text-[#555555] mb-2">开场白</label>
            <Textarea
              value={formData.greeting}
              onChange={(e) => handleInputChange('greeting', e.target.value)}
              placeholder="请输入开场白（最多100字）"
              rows={3}
              maxLength={100}
              className="w-full bg-white placeholder:text-[#C7C7C7]"
            />
            <div className="text-xs text-gray-400 mt-1 text-right">
              {formData.greeting.length}/100
            </div>
          </div>

          {/* 提问灵感 */}
          <div>
            <label className="block text-m text-[#555555] mb-2">提问灵感</label>
            <div className="space-y-2">
              {formData.questionSuggestions.map((suggestion, index) => (
                <div key={index} className="flex w-full items-center space-x-3">
                  <Input
                    value={suggestion}
                    onChange={(e) =>
                      handleQuestionSuggestionChange(index, e.target.value)
                    }
                    placeholder="请输入提问灵感"
                    className="flex-1 w-[660px] placeholder:text-[#C7C7C7]"
                  />
                  {index > 0 && (
                    <button
                      type="button"
                      onClick={() => handleRemoveQuestionSuggestion(index)}
                      title="删除提问灵感"
                      aria-label="删除提问灵感"
                    >
                      <Icon name="minus" size={25} />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={handleAddQuestionSuggestion}
                    title="添加提问灵感"
                    aria-label="添加提问灵感"
                  >
                    <Icon name="plus" size={25} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* 大模型选择 */}
          <div>
            <label className="block text-m text-[#555555] mb-2">
              大模型选择
            </label>
            <Select
              value={formData.model.toString()}
              onValueChange={(value) => handleInputChange('model', value)}
            >
              <SelectTrigger className="w-full h-9 bg-white rounded-md border border-input px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring">
                <SelectValue placeholder="请选择大模型" />
              </SelectTrigger>
              {modelOptions && (
                <SelectContent>
                  {modelOptions.map((option) => (
                    <SelectItem
                      key={option.value}
                      value={option.value.toString()}
                      disabled={option.value !== 0}
                    >
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              )}
            </Select>
          </div>

          {/* 生成随机性 */}
          <div>
            <label className="block text-m text-[#555555] mb-2">
              生成随机性
            </label>
            <div className="flex items-center space-x-4">
              <Slider
                value={[formData.temperature]}
                onValueChange={(value: number[]) =>
                  handleInputChange('temperature', value[0])
                }
                min={0}
                max={1}
                step={0.1}
                className="w-[320px]"
              />
              <NumberInput
                value={formData.temperature}
                onChange={(value) => handleInputChange('temperature', value)}
                min={0}
                max={1}
                step={0.1}
              />
            </div>
          </div>

          {/* 携带上下文轮数 */}
          <div>
            <label className="block text-m text-[#555555] mb-2">
              携带上下文轮数
            </label>
            <div className="flex items-center space-x-4">
              <Slider
                value={[formData.roundsContext]}
                onValueChange={(value: number[]) =>
                  handleInputChange('roundsContext', value[0])
                }
                min={1}
                max={20}
                step={1}
                className="w-[320px]"
              />
              <NumberInput
                value={formData.roundsContext}
                onChange={(value) => handleInputChange('roundsContext', value)}
                min={1}
                max={20}
                step={1}
              />
            </div>
          </div>

          {/* 最大回复长度 */}
          <div>
            <label className="block text-m text-[#555555] mb-2">
              最大回复长度
            </label>
            <div className="flex items-center space-x-4">
              <Slider
                value={[formData.maxReplyTokens]}
                onValueChange={(value: number[]) =>
                  handleInputChange('maxReplyTokens', value[0])
                }
                min={500}
                max={4000}
                step={100}
                className="w-[320px]"
              />
              <NumberInput
                value={formData.maxReplyTokens}
                onChange={(value) => handleInputChange('maxReplyTokens', value)}
                min={500}
                max={4000}
                step={100}
              />
            </div>
          </div>

          {/* 技能 */}
          <div>
            <label className="block text-m text-[#555555] mb-2">技能</label>
            <div className="flex flex-wrap gap-3">
              {skillOptions.map((skill) => (
                <button
                  key={skill.id}
                  type="button"
                  onClick={() => handleSkillToggle(skill.id)}
                  className={`h-[30px] w-[88px] border rounded-lg text-sm transition-all duration-200 ${
                    formData.skills.includes(skill.id)
                      ? 'border-[#8752FF] bg-[#8752FF1F] text-[#8752FF]'
                      : 'border-[#E9DFFF] text-[#555555] hover:border-[#8752FF] hover:bg-[#8752FF0A]'
                  }`}
                >
                  {skill.label}
                </button>
              ))}
            </div>
          </div>

          {/* 知识库 */}
          <div>
            <label className="block text-m text-[#555555] mb-2">知识库</label>
            <div className="flex flex-col space-y-2">
              {knowledgeBaseOptions.map((option) => (
                <div
                  key={option.value}
                  className={`w-full p-3 rounded-md border transition-all duration-200 ${
                    option.disabled
                      ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-50'
                      : formData.knowledgeBase === option.value
                      ? 'border-[rgba(155,112,248,0.69)] bg-white cursor-pointer'
                      : 'border-[rgba(233,223,255,0.32)] bg-white cursor-pointer'
                  }`}
                  onClick={() => {
                    if (!option.disabled) {
                      handleInputChange('knowledgeBase', option.value)
                    }
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Radio
                        name="knowledgeBase"
                        value={option.value}
                        checked={formData.knowledgeBase === option.value}
                        disabled={option.disabled}
                        onChange={(e) => {
                          if (e.target.checked && !option.disabled) {
                            handleInputChange('knowledgeBase', option.value)
                          }
                        }}
                        label=""
                      />
                      <span
                        className={`text-sm ${
                          option.disabled ? 'text-gray-400' : 'text-gray-700'
                        }`}
                      >
                        {option.label}
                        {option.disabled && (
                          <span className="ml-1 text-xs text-gray-400">
                            (即将开放)
                          </span>
                        )}
                      </span>
                    </div>
                    {!option.disabled && (
                      <Button
                        variant="ghost"
                        className="rounded-full text-xs"
                        onClick={handleKnowledgeDialogOpen}
                      >
                        +添加
                      </Button>
                    )}
                  </div>
                  {selectedKnowledgeBases.length > 0 &&
                    formData.knowledgeBase === option.value && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {selectedKnowledgeBases.map((knowledge) => (
                          <div
                            key={knowledge.id}
                            className="w-full flex items-center space-x-2 bg-white border border-gray-200 rounded-lg px-3 py-2"
                          >
                            <div className="flex-1 flex items-center space-x-2">
                              <div className="w-5 h-5 flex items-center justify-center">
                                <Icon
                                  name={
                                    knowledge.docType === 1 ? 'text' : 'table'
                                  }
                                  size={40}
                                  className="w-full h-full"
                                />
                              </div>
                              <span className="text-sm text-gray-700">
                                {knowledge.name}
                              </span>
                            </div>
                            <button
                              className="w-4 h-4 flex items-center justify-center"
                              type="button"
                              onClick={() =>
                                handleRemoveKnowledge(knowledge.id)
                              }
                              title="删除知识库"
                              aria-label="删除知识库"
                            >
                              <Icon name="minus" size={25} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* 知识库选择对话框 */}
      <KnowledgeBaseDialog
        open={knowledgeDialogOpen}
        onOpenChange={setKnowledgeDialogOpen}
        selectedDocType={
          formData.knowledgeBase === KnowledgeDocType.TEXT ? 'text' : 'table'
        }
        onConfirm={handleKnowledgeConfirm}
        existingKnowledgeBases={selectedKnowledgeBases}
      />
    </div>
  )
}

export default AgentForm
